// The density heatmap: where hittable bullets have been over about the last second. Positions
// go into a coarse grid each tick and decay, and the grid draws as a translucent colour layer
// under the field, on its own small canvas that CSS scales up smoothly.
import type { Bullets } from './sim.ts';

const CAN_HIT = 4;
/** Grid cell size in field units. */
export const CELL = 8;
/** Per-tick decay, so a cell keeps 5% of a bullet's count after one second. */
const DECAY = 0.05 ** (1 / 60);
/** Bullets per cell at which the colour saturates. */
const SATURATE = 1.5;

// Inferno, sampled every tenth: perceptually even, and readable on black.
const INFERNO = [
  [0, 0, 4],
  [22, 11, 57],
  [66, 10, 104],
  [106, 23, 110],
  [147, 38, 103],
  [188, 55, 84],
  [221, 81, 58],
  [243, 120, 25],
  [252, 165, 10],
  [246, 215, 70],
  [252, 255, 164],
];

/** 256 RGB entries interpolated from the samples above. */
const LUT = (() => {
  const lut = new Uint8ClampedArray(256 * 3);
  for (let i = 0; i < 256; i++) {
    const t = (i / 255) * (INFERNO.length - 1);
    const k = Math.min(INFERNO.length - 2, Math.floor(t));
    const f = t - k;
    for (let c = 0; c < 3; c++) lut[i * 3 + c] = INFERNO[k][c] + (INFERNO[k + 1][c] - INFERNO[k][c]) * f;
  }
  return lut;
})();

export class Heat {
  /** Bullets per cell, averaged over about the last second. */
  peak = 0;

  private canvas: HTMLCanvasElement;
  private g: CanvasRenderingContext2D;
  private grid = new Float32Array(0);
  private cols = 0;
  private rows = 0;
  private image: ImageData | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const g = canvas.getContext('2d');
    if (!g) throw new Error('Canvas 2D is not available');
    this.g = g;
  }

  /**
   * Matches the grid to a field of w × h units, starting empty if that changed, and lays the
   * canvas over the field's box on screen. The last column can run past the right edge.
   */
  fit(w: number, h: number, field: DOMRect): void {
    const cols = Math.ceil(w / CELL);
    const rows = Math.ceil(h / CELL);
    if (cols !== this.cols || rows !== this.rows) {
      this.cols = cols;
      this.rows = rows;
      this.grid = new Float32Array(cols * rows);
      this.canvas.width = cols;
      this.canvas.height = rows;
      this.image = this.g.createImageData(cols, rows);
    }
    const scale = field.height / h;
    const style = this.canvas.style;
    style.left = `${field.left}px`;
    style.top = `${field.top}px`;
    style.width = `${cols * CELL * scale}px`;
    style.height = `${rows * CELL * scale}px`;
  }

  clear(): void {
    this.grid.fill(0);
    this.peak = 0;
  }

  /** One tick: decay everything, then add each hittable bullet to its cell. */
  add(view: Bullets): void {
    const grid = this.grid;
    for (let i = 0; i < grid.length; i++) grid[i] *= DECAY;
    const cols = this.cols;
    const rows = this.rows;
    for (let i = 0; i < view.count; i++) {
      if ((view.flags[i] & CAN_HIT) === 0) continue;
      const cx = Math.floor(view.x[i] / CELL);
      const cy = rows - 1 - Math.floor(view.y[i] / CELL);
      if (cx < 0 || cx >= cols || cy < 0 || cy >= rows) continue;
      grid[cy * cols + cx] += 1;
    }
    // At a steady rate the grid holds rate / (1 - DECAY); scale back to bullets per cell.
    let peak = 0;
    for (let i = 0; i < grid.length; i++) if (grid[i] > peak) peak = grid[i];
    this.peak = peak * (1 - DECAY);
  }

  /** Paints the grid. Empty cells stay clear; denser ones go from faint purple to pale yellow. */
  draw(): void {
    if (!this.image) return;
    const data = this.image.data;
    const grid = this.grid;
    const scale = 1 - DECAY;
    for (let i = 0; i < grid.length; i++) {
      const v = Math.min(1, Math.sqrt((grid[i] * scale) / SATURATE));
      // Skip the near-black end of the scale, which wouldn't show on the black field, and fade
      // in with density so a lone bullet's trail stays faint.
      const idx = Math.round((0.15 + 0.85 * v) * 255) * 3;
      const o = i * 4;
      data[o] = LUT[idx];
      data[o + 1] = LUT[idx + 1];
      data[o + 2] = LUT[idx + 2];
      data[o + 3] = Math.round(255 * Math.min(0.8, 1.2 * v));
    }
    this.g.putImageData(this.image, 0, 0);
  }
}
